import { useState, useEffect } from "react";
import { useDispatch } from "react-redux";
import { getTaskBySlug, submitTask } from "../../features/courses/courseSlice";
import { FiCheckCircle, FiXCircle, FiShield, FiAlertCircle } from "react-icons/fi";

// Pull the grader's human message out of the submit response. Integration graders
// put it on the top-level `message`; fall back to the TestResultException payload.
function extractMessage(result) {
  if (result?.message) return result.message;
  const em = result?.data?.exceptionMessage;
  if (typeof em === "string" && em.includes("TestResultException")) {
    try {
      const arr = JSON.parse(em.replace("TestResultException: ", ""));
      if (Array.isArray(arr) && arr[0]?.message) return arr[0].message;
    } catch (e) {
      /* ignore */
    }
  }
  return null;
}

// Inline "Verify" card for integration tasks (metadata-check / request-log /
// inbound-probe). No code editor — the student clicks Verify and the server-side
// grader checks their org / the request log / their Flow.
function IntegrationVerify({ taskSlug, taskNumber, onProgressUpdate }) {
  const dispatch = useDispatch();
  const [task, setTask] = useState(null);
  const [status, setStatus] = useState("idle"); // idle | verifying | pass | fail | error
  const [message, setMessage] = useState(null);
  const [completed, setCompleted] = useState(false);

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const res = await dispatch(getTaskBySlug(taskSlug)).unwrap();
        if (!active) return;
        setTask(res.data.task);
        if (res.data.progress?.status === "Completed") setCompleted(true);
      } catch (err) {
        console.error("Error loading task:", err);
      }
    })();
    return () => {
      active = false;
    };
  }, [taskSlug, dispatch]);

  const handleVerify = async () => {
    if (!task) return;
    setStatus("verifying");
    setMessage(null);
    try {
      const result = await dispatch(
        submitTask({ taskId: task._id, apexBody: "", lessonType: task.type, runTestOnly: false })
      ).unwrap();
      const passed = result?.data?.success === true;
      setStatus(passed ? "pass" : "fail");
      setMessage(extractMessage(result) || (passed ? "Verified!" : "Not verified yet — check the steps above and try again."));
      if (passed) setCompleted(true);
      if (onProgressUpdate && result.submissionResults) {
        onProgressUpdate(taskSlug, result.submissionResults);
      }
    } catch (err) {
      setStatus("error");
      const data = err?.response?.data || err;
      setMessage(
        data?.message ||
          "Couldn't verify. Make sure you're logged in to your Salesforce org, then try again."
      );
    }
  };

  const isDone = completed || status === "pass";
  const accent = isDone
    ? "rgba(134, 178, 122, 0.5)"
    : status === "fail" || status === "error"
    ? "rgba(239, 68, 68, 0.45)"
    : "rgba(165, 138, 194, 0.4)";

  return (
    <div
      className="rounded-xl overflow-hidden my-5"
      style={{ background: "rgba(39, 39, 42, 0.6)", border: `1px solid ${accent}` }}
    >
      <div className="px-4 py-3 flex items-center justify-between gap-3">
        <div className="flex items-center gap-2 min-w-0">
          <FiShield className="w-4 h-4 flex-shrink-0" style={{ color: "rgba(184, 168, 214, 1)" }} />
          <span className="text-sm font-semibold text-white truncate">
            {taskNumber ? `Verify ${taskNumber}: ` : "Verify: "}
            {task ? task.name : "…"}
          </span>
          {isDone && (
            <FiCheckCircle className="w-4 h-4 flex-shrink-0" style={{ color: "#86b27a" }} title="Completed" />
          )}
        </div>
        <button
          onClick={handleVerify}
          disabled={!task || status === "verifying"}
          className="px-4 py-2 rounded-lg font-medium text-sm text-white transition-all disabled:opacity-50 disabled:cursor-not-allowed flex-shrink-0"
          style={{
            background:
              status === "verifying"
                ? "rgba(165, 138, 194, 0.4)"
                : "linear-gradient(135deg, rgba(165, 138, 194, 0.75), rgba(145, 118, 174, 0.75))",
            border: "1px solid rgba(165, 138, 194, 0.4)",
          }}
        >
          {status === "verifying" ? "Verifying…" : isDone ? "Re-verify" : "Verify"}
        </button>
      </div>
      {message && (
        <div
          className="px-4 py-2 text-sm border-t flex items-start gap-2"
          style={{
            borderColor: "rgba(113, 113, 122, 0.25)",
            color: isDone ? "rgba(184, 212, 168, 1)" : "rgba(252, 165, 165, 1)",
          }}
        >
          {isDone ? (
            <FiCheckCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
          ) : status === "error" ? (
            <FiAlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
          ) : (
            <FiXCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
          )}
          <span>{message}</span>
        </div>
      )}
    </div>
  );
}

export default IntegrationVerify;
